import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback || (
        <div className="flex flex-col items-center justify-center h-full p-4 bg-[#1a2a2a]/70 text-center rounded-lg">
          <AlertTriangle size={32} className="text-red-400 mb-2" />
          <h3 className="text-lg font-medium text-white mb-1">Something went wrong</h3>
          <p className="text-sm text-gray-400 mb-3">
            {this.state.error?.message || "The widget encountered an issue while rendering"}
          </p>
          <button
            className="px-3 py-1 bg-[#47be8f] text-black rounded text-sm"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
